"use client";

import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useEffect,
  useState,
  type ReactNode,
  type Dispatch,
} from "react";

export type SelectedOption = {
  groupName: string;
  items: { name: string; priceDelta: number; quantity?: number }[];
};

export type CartItem = {
  id: string; // unique key for this line (productId + options hash)
  productId: number;
  productName: string;
  productPrice: number; // base price in cents (after discount)
  quantity: number;
  selectedOptions: SelectedOption[];
  unitTotal: number; // productPrice + sum of option deltas, in cents
  maxQuantity?: number | null; // max units per order (null = unlimited)
};

type CartState = {
  items: CartItem[];
};

type CartAction =
  | { type: "ADD_ITEM"; item: CartItem }
  | { type: "REMOVE_ITEM"; id: string }
  | { type: "REPLACE_ITEM"; oldId: string; item: CartItem }
  | { type: "UPDATE_QUANTITY"; id: string; quantity: number }
  | { type: "UPDATE_PRICE"; productId: number; newPrice: number }
  | { type: "CLEAR_CART" }
  | { type: "HYDRATE"; items: CartItem[] };

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "ADD_ITEM": {
      const existing = state.items.find((i) => i.id === action.item.id);
      if (existing) {
        return {
          items: state.items.map((i) =>
            i.id === action.item.id
              ? { ...i, quantity: i.quantity + action.item.quantity }
              : i
          ),
        };
      }
      return { items: [...state.items, action.item] };
    }
    case "REMOVE_ITEM":
      return { items: state.items.filter((i) => i.id !== action.id) };
    case "REPLACE_ITEM": {
      if (action.oldId === action.item.id) {
        // Same options — just update in place
        return {
          items: state.items.map((i) =>
            i.id === action.oldId ? action.item : i
          ),
        };
      }
      // Options changed — check if new ID already exists
      const existingNew = state.items.find((i) => i.id === action.item.id);
      if (existingNew) {
        // Merge quantity into existing line and remove old
        return {
          items: state.items
            .filter((i) => i.id !== action.oldId)
            .map((i) =>
              i.id === action.item.id
                ? { ...i, quantity: i.quantity + action.item.quantity }
                : i
            ),
        };
      }
      // Replace old with new
      return {
        items: state.items.map((i) =>
          i.id === action.oldId ? action.item : i
        ),
      };
    }
    case "UPDATE_QUANTITY":
      if (action.quantity <= 0) {
        return { items: state.items.filter((i) => i.id !== action.id) };
      }
      return {
        items: state.items.map((i) =>
          i.id === action.id ? { ...i, quantity: action.quantity } : i
        ),
      };
    case "UPDATE_PRICE":
      return {
        items: state.items.map((i) => {
          if (i.productId !== action.productId) return i;
          const optionsDelta = i.unitTotal - i.productPrice;
          return { ...i, productPrice: action.newPrice, unitTotal: action.newPrice + optionsDelta };
        }),
      };
    case "CLEAR_CART":
      return { items: [] };
    case "HYDRATE":
      return { items: action.items };
    default:
      return state;
  }
}

/** Build a stable ID for a cart line from product + selected options */
export function buildCartItemId(
  productId: number,
  selectedOptions: SelectedOption[]
): string {
  const optKey = selectedOptions
    .map((g) => `${g.groupName}:${g.items.map((i) => {
      const qty = i.quantity ?? 1;
      return qty > 1 ? `${qty}x${i.name}` : i.name;
    }).sort().join(",")}`)
    .sort()
    .join("|");
  return `${productId}::${optKey}`;
}

type CartContextValue = {
  cart: CartItem[];
  dispatch: Dispatch<CartAction>;
  cartTotal: number;
  cartCount: number;
  lastAddedAt: number;
  hydrated: boolean;
};

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({
  slug,
  children,
}: {
  slug: string;
  children: ReactNode;
}) {
  const storageKey = `cart:${slug}`;
  const [state, rawDispatch] = useReducer(cartReducer, { items: [] });
  const [lastAddedAt, setLastAddedAt] = useState(0);
  const [hydrated, setHydrated] = useState(false);

  const dispatch: Dispatch<CartAction> = useCallback((action) => {
    rawDispatch(action);
    if (action.type === "ADD_ITEM") {
      setLastAddedAt(Date.now());
    }
  }, []);

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const items = JSON.parse(saved) as CartItem[];
        if (Array.isArray(items) && items.length > 0) {
          dispatch({ type: "HYDRATE", items });
        }
      }
    } catch {
      // ignore corrupt data
    }
    setHydrated(true);
  }, [storageKey]);

  // Persist to localStorage on change
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(state.items));
  }, [state.items, storageKey]);

  const cartTotal = state.items.reduce(
    (sum, item) => sum + item.unitTotal * item.quantity,
    0
  );
  const cartCount = state.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext value={{ cart: state.items, dispatch, cartTotal, cartCount, lastAddedAt, hydrated }}>
      {children}
    </CartContext>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
